'use client';
import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Typography, Picture, DotsIndicator } from '@workspace/ui/common';
import type { LogoWallProps } from './logo-wall.types';
import { cn } from '@workspace/lib';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { stripInvisibles } from '@workspace/constants';
import { getBackgroundClass } from '@workspace/lib';
import { getTextAlignClass } from './layout';
import { useMediaQuery } from '@workspace/hooks/commons/useMediaQuery';
import { theme } from '@workspace/ui/theme';

export function LogoWall({ block }: LogoWallProps) {
  const { backgroundColor, heading, description, logos, textAlignment } = block;
  const textAlignClass = getTextAlignClass(textAlignment);
  const backgroundClass = getBackgroundClass(backgroundColor);
  const textHeadingColorClass = theme === 'redRec' ? 'text-primary' : '';
  const texDescriptionColorClass = theme === 'redRec' ? 'text-secondary-500' : 'text-secondary-600';

  const originalLogos = Array.isArray(logos) ? logos : [];
  const isMobile = useMediaQuery('(max-width: 992px)');

  // Determine if carousel should be disabled based on screen size
  const shouldDisableCarousel = isMobile ? originalLogos.length <= 3 : originalLogos.length <= 5;

  let displayLogos = originalLogos;

  // Only duplicate if we have enough logos and carousel is enabled
  if (!shouldDisableCarousel && originalLogos.length > 5) {
    const repeatCount = Math.ceil(12 / originalLogos.length);
    displayLogos = Array(repeatCount).fill(originalLogos).flat();
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !shouldDisableCarousel && originalLogos.length >= 5,
    align: shouldDisableCarousel ? 'center' : 'start',
    containScroll: shouldDisableCarousel ? 'trimSnaps' : false,
    dragFree: !shouldDisableCarousel,
    slidesToScroll: 1,
    watchDrag: !shouldDisableCarousel,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const lenRef = React.useRef(originalLogos.length);
  lenRef.current = originalLogos.length;

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap() % lenRef.current);
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  if (!displayLogos.length) return null;

  return (
    <div className={cn('w-full', backgroundClass)}>
      <div className="porta-container flex flex-col items-center gap-6 md:gap-12 px-6 lg:pt-20 py-8 lg:px-30">
        <div className={cn('flex flex-col gap-2 w-full', textAlignClass)}>
          {heading && (
            <Typography
              variant="h2"
              styleVariant="h3"
              className={cn('font-bold', textAlignClass, textHeadingColorClass)}
            >
              {heading}
            </Typography>
          )}

          {Array.isArray(description) && (
            <div className={cn('text-sm! leading-[1.42857] ', textAlignClass, texDescriptionColorClass)}>
              <CustomPortableText value={description} />
            </div>
          )}
        </div>

        {/* Logos Carousel */}
        <div className="flex justify-center w-full">
          <div className="embla w-full max-w-full overflow-hidden" ref={emblaRef}>
            <div
              className={cn(
                'embla__container flex items-center lg:gap-10 xl:gap-12',
                shouldDisableCarousel && 'justify-center',
              )}
            >
              {displayLogos.map((logo, index) => {
                const logoAsset = logo?.bynderLogoImage ?? logo;
                const logoUrl =
                  logoAsset?.selectedUrl || logoAsset?.url || logoAsset?.datUrl || logoAsset?.previewUrl || '';
                if (!logoUrl) return null;

                return (
                  <div
                    key={`${logo._key || 'logo'}-${index}`}
                    className={cn(
                      'embla__slide',
                      'grow-0 shrink-0 basis-1/3 lg:basis-1/6',
                      'max-md:flex max-md:justify-center',
                    )}
                  >
                    <div className="flex items-center justify-center relative lg:w-full w-fit">
                      <Picture
                        src={logoUrl}
                        alt={logo.bynderLogoImage?.alt || stripInvisibles(logo.alt) || 'Logo'}
                        desktopWidth={232}
                        desktopHeight={64}
                        className="w-full h-full"
                        imgClassName={cn(
                          'w-full max-w-25  sm:max-w-48 lg:max-w-58',
                          'h-10 sm:h-14 xl:h-16',
                          'object-contain!',
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {!shouldDisableCarousel && (
          <DotsIndicator
            className="justify-center pt-md md:pt-xl"
            count={originalLogos.length}
            currentIndex={selectedIndex}
            onDotClick={scrollTo}
          />
        )}
      </div>
    </div>
  );
}

export default LogoWall;
